/**
 * Tipos para DERAR2Geocoder
 *
 * Define interfaces para los datos DERA servidos por el Worker R2.
 * DERA = Datos Espaciales de Referencia de Andalucía
 *
 * @module types/deraR2
 * @version 1.0.0
 */

// =============================================================================
// DATOS DEL WORKER R2
// =============================================================================

/** Tipologías de infraestructura DERA */
export type DERATipologia =
  | 'SANITARIO'
  | 'EDUCATIVO'
  | 'SEGURIDAD'
  | 'CULTURAL'
  | 'DEPORTIVO'
  | 'ADMINISTRATIVO'
  | 'SOCIAL'
  | 'TRANSPORTE'
  | 'COMUNICACIONES'
  | 'ENERGIA'
  | 'AGUA'
  | 'COMBUSTIBLE'
  | 'RESIDUOS';

/** Infraestructura DERA servida por Worker R2 */
export interface DERAR2Feature {
  /** ID único DERA (ej: "DERA-04001-SAL-001") */
  id: string;
  /** Nombre de la infraestructura */
  nombre: string;
  /** Tipología principal */
  tipologia: DERATipologia;
  /** Subtipo específico (ej: "CENTRO_SALUD", "HOSPITAL") */
  subtipo: string;
  /** Coordenada X UTM30 EPSG:25830 */
  x: number;
  /** Coordenada Y UTM30 EPSG:25830 */
  y: number;
  /** Nombre del municipio */
  municipio: string;
  /** Código INE 5 dígitos */
  cod_ine: string;
  /** Nombre de la provincia */
  provincia: string;
  /** Código provincia 2 dígitos */
  cod_prov: string;
  /** Dirección postal (opcional) */
  direccion?: string;
  /** Fuente de datos */
  fuente: string;
}

// =============================================================================
// OPCIONES DE GEOCODIFICACIÓN
// =============================================================================

/** Opciones para búsqueda en DERA R2 */
export interface DERAR2SearchOptions {
  /** Código INE municipio (5 dígitos) - REQUERIDO */
  codINE: string;
  /** Nombre infraestructura para fuzzy match */
  name?: string;
  /** Tipología para filtrar (string para flexibilidad con mapeo) */
  tipologia?: string;
  /** Subtipo específico */
  subtipo?: string;
  /** Umbral mínimo confianza (0-100, default: 60) */
  minConfidence?: number;
  /** Máximo resultados (default: 5) */
  maxResults?: number;
}

/** Resultado de geocodificación DERA R2 */
export interface DERAR2Result {
  /** Coordenada X UTM30 */
  x: number;
  /** Coordenada Y UTM30 */
  y: number;
  /** Confianza del resultado (0-100) */
  confidence: number;
  /** Precisión estimada en metros */
  precision: number;
  /** Fuente identificadora */
  source: 'dera_r2';
  /** Nivel en cascada */
  sourceLevel: 4;
  /** Feature DERA encontrada */
  feature: DERAR2Feature;
  /** Score de fuzzy matching (si aplica) */
  fuzzyScore?: number;
}

// =============================================================================
// CONSTANTES VALIDACIÓN UTM30 ANDALUCÍA
// =============================================================================

/** Rango válido X UTM30 Andalucía */
export const X_RANGE_ANDALUCIA = { min: 50000, max: 800000 }; // C3.01: Campo de Gibraltar ~764 000 m
/** Rango válido Y UTM30 Andalucía */
export const Y_RANGE_ANDALUCIA = { min: 3950000, max: 4300000 };

/** Lista de tipologías DERA disponibles */
export const DERA_TIPOLOGIAS: DERATipologia[] = [
  'SANITARIO',
  'EDUCATIVO',
  'SEGURIDAD',
  'CULTURAL',
  'DEPORTIVO',
  'ADMINISTRATIVO',
  'SOCIAL',
  'TRANSPORTE',
  'COMUNICACIONES',
  'ENERGIA',
  'AGUA',
  'COMBUSTIBLE',
  'RESIDUOS',
];
